package com.bikompanion.data.rating.firebase

import com.bikompanion.domain.BikeKey

/**
 * Builds and validates every Realtime Database path the rating feature touches.
 *
 * One place, because the scoping *is* the path here — `/bikeScores/{networkId}/{bikeNumber}` —
 * and a key segment that RTDB rejects would otherwise surface as an opaque permission failure
 * from the server rather than an obvious bug locally. The character set below mirrors what the
 * security rules enforce, so anything this accepts the rules accept too.
 *
 * The four trees are deliberately siblings rather than nested: reading a node in RTDB downloads
 * its whole subtree, so the append-only vote log must never live underneath the aggregate that
 * every score lookup reads.
 */
internal object RatingPaths {
    /** RTDB forbids `.`, `$`, `#`, `[`, `]`, `/` and control characters in keys — stricter than Firestore. */
    private val NetworkIdPattern = Regex("^[a-z0-9-]{1,64}$")
    private val BikeNumberPattern = Regex("^[0-9]{1,12}$")

    fun scores(key: BikeKey): String = "bikeScores/${key.validNetworkId()}/${key.validBikeNumber()}"

    fun votes(key: BikeKey): String = "bikeVotes/${key.validNetworkId()}/${key.validBikeNumber()}"

    fun guard(
        uid: String,
        key: BikeKey,
    ): String = "voteGuards/$uid/${key.validNetworkId()}/${key.validBikeNumber()}"

    /**
     * A rider's own votes, the same records as [votes] filed under them instead of under the bike.
     *
     * Needed because a rider cannot read their history out of [votes]: RTDB grants reads at or
     * above the node being queried, and that tree is sharded by bike, so listing one rider's votes
     * would mean granting read across everyone's. Push ids sort chronologically, so a history
     * screen is `orderByKey().limitToLast(n)` with no index and no query rules.
     *
     * The uid comes from Firebase Auth rather than from user input, so there is nothing to
     * validate — but it still goes through here so every path in this feature is built in one place.
     */
    fun myVotes(uid: String): String = "myVotes/$uid"

    /** True when [key] can be turned into a path at all. Callers that can't throw check this first. */
    fun isValid(key: BikeKey): Boolean = NetworkIdPattern.matches(key.networkId) && BikeNumberPattern.matches(key.bikeNumber)

    private fun BikeKey.validNetworkId(): String {
        require(NetworkIdPattern.matches(networkId)) { "Not a usable network id: '$networkId'" }
        return networkId
    }

    private fun BikeKey.validBikeNumber(): String {
        require(BikeNumberPattern.matches(bikeNumber)) { "Not a usable bike number: '$bikeNumber'" }
        return bikeNumber
    }
}
